import type { FC, ReactNode } from 'react'
import { useCallback, useState } from 'react'
import type { SxProps, Theme } from '@mui/material'
import { Box, Button, useTheme } from '@mui/material'
import { ExpandLess, ExpandMore } from '@mui/icons-material'

interface CollapsibleContentProps {
  readonly children: ReactNode
  readonly shouldCollapse: boolean
  readonly expanded?: boolean
  readonly defaultExpanded?: boolean
  readonly onExpandChange?: (expanded: boolean) => void
}

export const CollapsibleContent: FC<CollapsibleContentProps> = ({
  children,
  shouldCollapse,
  expanded: controlledExpanded,
  defaultExpanded = false,
  onExpandChange,
}) => {
  const theme = useTheme()
  const [internalExpanded, setInternalExpanded] = useState(defaultExpanded)
  const isControlled = controlledExpanded !== undefined
  const expanded = isControlled ? controlledExpanded : internalExpanded

  const handleToggle = useCallback(() => {
    const newExpanded = !expanded
    if (!isControlled) {
      setInternalExpanded(newExpanded)
    }
    onExpandChange?.(newExpanded)
  }, [expanded, isControlled, onExpandChange])

  if (!shouldCollapse) {
    return <>{children}</>
  }

  return (
    <Box sx={containerSx}>
      {children}

      <Box sx={buttonContainerSx}>
        <Button
          size="small"
          variant="text"
          onClick={handleToggle}
          startIcon={expanded ? <ExpandLess /> : <ExpandMore />}
          sx={toggleButtonSx(theme)}
        >
          {expanded ? 'Collapse' : 'Show all'}
        </Button>
      </Box>
    </Box>
  )
}

const containerSx: SxProps<Theme> = {
  position: 'relative',
}

const buttonContainerSx: SxProps<Theme> = {
  display: 'flex',
  justifyContent: 'flex-start',
  alignItems: 'center',
  mt: 0.5,
  px: 1,
}

const toggleButtonSx = (theme: Theme): SxProps<Theme> => ({
  textTransform: 'none',
  color: theme.palette.primary.main,
  '&:hover': {
    backgroundColor: theme.palette.action.hover,
  },
})
